import { ActionTiles } from '@ps/isp-web-component-library';
import { BetBuilderBadgeAll } from '@ps/isp-web-component-library/icons/BetBuilderBadgeAll';
import { BetBuilderBadgeAllActive } from '@ps/isp-web-component-library/icons/BetBuilderBadgeAllActive';
import { BetBuilderBadgeCorners } from '@ps/isp-web-component-library/icons/BetBuilderBadgeCorners';
import { BetBuilderBadgeCornersActive } from '@ps/isp-web-component-library/icons/BetBuilderBadgeCornersActive';
import { BetBuilderBadgeGoals } from '@ps/isp-web-component-library/icons/BetBuilderBadgeGoals';
import { BetBuilderBadgeGoalsActive } from '@ps/isp-web-component-library/icons/BetBuilderBadgeGoalsActive';
import { BetBuilderBadgePlayers } from '@ps/isp-web-component-library/icons/BetBuilderBadgePlayers';
import { BetBuilderBadgePlayersActive } from '@ps/isp-web-component-library/icons/BetBuilderBadgePlayersActive';
import { BetBuilderBadgeResult } from '@ps/isp-web-component-library/icons/BetBuilderBadgeResult';
import { BetBuilderBadgeResultActive } from '@ps/isp-web-component-library/icons/BetBuilderBadgeResultActive';
import { BetBuilderBadgeYellowCards } from '@ps/isp-web-component-library/icons/BetBuilderBadgeYellowCards';
import { BetBuilderBadgeYellowCardsActive } from '@ps/isp-web-component-library/icons/BetBuilderBadgeYellowCardsActive';
import { SubMarketGroupType } from '@ps/isp-web-services-types';
import { useTrackers } from '@ps/web-shared-analytics';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import type { PropsWithoutRef, SVGProps } from 'react';
import { useSelector } from 'react-redux';

import { useSportsEventPageWidget } from '../../../../model';
import type { ClientMarketGroup } from '../../../../model/types';

import {
  TXTWCL_All_53,
  TXTWCL_Cards,
  TXTWCL_Corners_2,
  TXTWCL_Goals_1,
  TXTWCL_Player_48,
  TXTWCL_Result_5
} from './MarketGroupActionTiles.messages.json';

interface SubMarketGroupIcons {
  default: React.ElementType<PropsWithoutRef<SVGProps<SVGSVGElement>>>;
  active: React.ElementType<PropsWithoutRef<SVGProps<SVGSVGElement>>>;
  translation: {
    id: string;
    defaultMessage: string;
  };
  key: SubMarketGroupType;
}

const getSubMarketGroupIcons: Record<SubMarketGroupType, SubMarketGroupIcons> = {
  [SubMarketGroupType.ALL]: {
    default: BetBuilderBadgeAll,
    active: BetBuilderBadgeAllActive,
    translation: TXTWCL_All_53,
    key: SubMarketGroupType.ALL
  },
  [SubMarketGroupType.PLAYER]: {
    default: BetBuilderBadgePlayers,
    active: BetBuilderBadgePlayersActive,
    translation: TXTWCL_Player_48,
    key: SubMarketGroupType.PLAYER
  },
  [SubMarketGroupType.RESULT]: {
    default: BetBuilderBadgeResult,
    active: BetBuilderBadgeResultActive,
    translation: TXTWCL_Result_5,
    key: SubMarketGroupType.RESULT
  },
  [SubMarketGroupType.GOALS]: {
    default: BetBuilderBadgeGoals,
    active: BetBuilderBadgeGoalsActive,
    translation: TXTWCL_Goals_1,
    key: SubMarketGroupType.GOALS
  },
  [SubMarketGroupType.CORNERS]: {
    default: BetBuilderBadgeCorners,
    active: BetBuilderBadgeCornersActive,
    translation: TXTWCL_Corners_2,
    key: SubMarketGroupType.CORNERS
  },
  [SubMarketGroupType.CARDS]: {
    default: BetBuilderBadgeYellowCards,
    active: BetBuilderBadgeYellowCardsActive,
    translation: TXTWCL_Cards,
    key: SubMarketGroupType.CARDS
  }
};

interface Props {
  marketGroup: ClientMarketGroup;
  onTileSelected: (type: SubMarketGroupType | undefined) => void;
}

export const MarketGroupActionTiles: React.FC<Props> = ({ marketGroup, onTileSelected }) => {
  const { widgetSelect } = useSportsEventPageWidget();
  const { tabClicked } = useTrackers();
  const allLabel = useTrackingLabel(TXTWCL_All_53);
  const playerLabel = useTrackingLabel(TXTWCL_Player_48);
  const resultLabel = useTrackingLabel(TXTWCL_Result_5);
  const goalsLabel = useTrackingLabel(TXTWCL_Goals_1);
  const cornersLabel = useTrackingLabel(TXTWCL_Corners_2);
  const cardsLabel = useTrackingLabel(TXTWCL_Cards);

  const tabLabels: Record<SubMarketGroupType, string> = {
    [SubMarketGroupType.ALL]: allLabel || 'All',
    [SubMarketGroupType.PLAYER]: playerLabel || 'Player',
    [SubMarketGroupType.RESULT]: resultLabel || 'Result',
    [SubMarketGroupType.GOALS]: goalsLabel || 'Goals',
    [SubMarketGroupType.CORNERS]: cornersLabel || 'Corners',
    [SubMarketGroupType.CARDS]: cardsLabel || 'Cards'
  };

  const handleTileClick = (key?: SubMarketGroupType) => {
    const tabLabel = key ? tabLabels[key] : 'All';
    tabClicked({ tabName: tabLabel });
    onTileSelected(key);
  };
  const selectedSubMarketGroupType = useSelector(widgetSelect.selectedSubMarketGroupType);

  const { groupedTemplates } = marketGroup;

  if (!groupedTemplates || !groupedTemplates.length) return null;

  const allMarkets = getSubMarketGroupIcons[SubMarketGroupType.ALL];

  return (
    <ActionTiles>
      <ActionTiles.Tile
        key={allMarkets.key}
        active={
          selectedSubMarketGroupType ? selectedSubMarketGroupType === SubMarketGroupType.ALL : true
        }
        activeIcon={allMarkets.active}
        defaultIcon={allMarkets.default}
        onClick={() => handleTileClick(undefined)}
      >
        <Translation {...allMarkets.translation} />
      </ActionTiles.Tile>
      {groupedTemplates.map(({ type }) => {
        const icons = getSubMarketGroupIcons[type];
        const isActive = selectedSubMarketGroupType === type;

        return (
          <ActionTiles.Tile
            active={isActive}
            key={icons.key}
            activeIcon={icons.active}
            defaultIcon={icons.default}
            onClick={() => handleTileClick(type)}
          >
            <Translation {...icons.translation} />
          </ActionTiles.Tile>
        );
      })}
    </ActionTiles>
  );
};
